import { useState } from 'react';
import axios from 'axios';

// Quotes tab — every saved quote for this contact (documents of type
// 'Quote'), newest first, each viewable as a PDF on demand and deletable
// (cleanup of test / obsolete quotes; delete is admin/staff only on the
// server). The PDF is rebuilt from the quote's stored snapshot via
// POST /api/quotes/:id/regenerate, so it reproduces the original and
// survives redeploys.
//
// Shared by the Lead and Customer detail pages. Because a lead becomes a
// customer on the SAME contact record, a lead's quotes automatically
// appear here on the customer too — nothing is copied or lost.
//
// `onChanged` (optional) is called after a successful delete so the parent
// page can reload its data and the row disappears.
export default function QuotesTab({ items, onChanged }) {
  const [busy, setBusy] = useState({});      // id -> viewing
  const [deleting, setDeleting] = useState({}); // id -> deleting
  const [error, setError] = useState(null);

  const quotes = (items || [])
    .filter((d) => (d.type || '').toLowerCase() === 'quote')
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  // merge_snapshot may arrive as a parsed object (jsonb) or a JSON string
  // (text) depending on the column type — normalise to an object.
  function snapOf(d) {
    if (!d.merge_snapshot) return null;
    if (typeof d.merge_snapshot === 'string') {
      try { return JSON.parse(d.merge_snapshot); } catch (_) { return null; }
    }
    return d.merge_snapshot;
  }

  const fmt = (v) => (v ? new Date(v).toLocaleDateString() : '—');

  async function viewPdf(doc) {
    setError(null);
    setBusy((s) => ({ ...s, [doc.id]: true }));
    try {
      const res = await axios.post(`/api/quotes/${doc.id}/regenerate`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = err.message;
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) {}
      setError(`Couldn't open quote #${doc.id}: ${msg}`);
    } finally {
      setBusy((s) => ({ ...s, [doc.id]: false }));
    }
  }

  async function deleteQuote(doc) {
    const snap = snapOf(doc);
    const label = (snap && snap.quote_number) ? `quote ${snap.quote_number}` : `this quote`;
    if (!window.confirm(`Delete ${label}? This removes it permanently from this record and cannot be undone.`)) return;
    setError(null);
    setDeleting((s) => ({ ...s, [doc.id]: true }));
    try {
      await axios.delete(`/api/quotes/${doc.id}`);
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(`Couldn't delete quote #${doc.id}: ${msg}`);
      setDeleting((s) => ({ ...s, [doc.id]: false }));
    }
  }

  if (quotes.length === 0) {
    return (
      <p className="empty-tab">
        No quotes yet. Use “Generate Quote” on a site to create one — it’s saved here automatically.
      </p>
    );
  }

  return (
    <div className="table-wrap">
      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}
      <table className="data-table">
        <thead>
          <tr>
            <th>Quote #</th><th>Created</th><th>Prepared by</th><th>Sent</th>
            <th aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {quotes.map((d) => {
            const snap = snapOf(d);
            const qn = (snap && snap.quote_number) || '—';
            const by = (snap && (snap.prepared_by || snap.generated_by_name)) || '—';
            const viewing = !!busy[d.id];
            const del = !!deleting[d.id];
            return (
              <tr key={d.id}>
                <td className="cell-mono">{qn}</td>
                <td className="cell-mono">{fmt(d.created_at)}</td>
                <td>{by}</td>
                <td className="cell-mono">{fmt(d.sent_at)}</td>
                <td>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => viewPdf(d)}
                    disabled={viewing || del}
                    title="Open this quote as a PDF"
                  >
                    {viewing ? 'Opening…' : 'View PDF'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    style={{ marginLeft: 6, color: 'var(--color-bad)' }}
                    onClick={() => deleteQuote(d)}
                    disabled={del || viewing}
                    title="Delete this quote from this record"
                  >
                    {del ? 'Deleting…' : 'Delete'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
